import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { nlt } from '../services/api';
import type { AgentConfiguratorState } from '../types/agentConfigurator';
import type { NLTChatDialogMessage, NLTChatResponse, NLTChatType } from '../types/nlt';
import {
  prepareConnectorConfigForNltApi,
  resolveNltAdminConfigForApi,
  type NltAdminConfigApiPayload,
} from '../utils/nltAdminConfigPayload';
import { chatMessageRoleLabel, type NltCompareQuote, type NltCompareQuoteMessageRole } from '../utils/nltQuote';
import ChatMarkdownBody from './ui/ChatMarkdownBody';
import './NLTConfigCompareChats.css';

type CompareSide = 'before' | 'after';
export type ConfigCompareChatsMode = 'nlt' | 'optimization';

interface CompareMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface SelectionToolbarState {
  text: string;
  side: CompareSide;
  messageId?: string;
  messageRole?: NltCompareQuoteMessageRole;
  top: number;
  left: number;
}

interface NLTConfigCompareChatsProps {
  mode?: ConfigCompareChatsMode;
  integrationId: string | null;
  beforeConfig: AgentConfiguratorState | null;
  afterConfig: AgentConfiguratorState | null;
  onQuoteToNlt?: (quote: NltCompareQuote) => void;
  /** Changes when compared configs change — remounts chat history. */
  compareKey?: string;
  beforeSubtitle?: string;
  afterSubtitle?: string;
}

const MODE_COPY: Record<
  ConfigCompareChatsMode,
  {
    title: string;
    description: string;
    beforeTitle: string;
    beforeBadge: string;
    beforeSubtitle: string;
    afterTitle: string;
    afterBadge: string;
    afterSubtitle: string;
  }
> = {
  nlt: {
    title: 'NLT config test chats',
    description:
      'Compare responses from the base config and the config with accepted patch changes.',
    beforeTitle: 'Before',
    beforeBadge: 'Baseline',
    beforeSubtitle: 'Original config snapshot',
    afterTitle: 'After',
    afterBadge: 'Patched',
    afterSubtitle: 'Current config with changes',
  },
  optimization: {
    title: 'Optimization config test chats',
    description:
      'Compare the initial optimization config with the selected optimization step. Change the step in the visualization to update the After chat.',
    beforeTitle: 'Before',
    beforeBadge: 'Initial',
    beforeSubtitle: 'Baseline config before tuning',
    afterTitle: 'After',
    afterBadge: 'Optimized',
    afterSubtitle: 'Config from the selected optimization step',
  },
};

const CHAT_TYPE_BY_MODE: Record<ConfigCompareChatsMode, NLTChatType> = {
  nlt: 'nlt_chat',
  optimization: 'ct_chat',
};

function extractReplyText(response: NLTChatResponse | null | undefined): string {
  const candidates = [
    response?.content,
    response?.reply,
    response?.message,
    response?.answer,
    response?.text,
    response?.response,
    response?.final_message,
  ];
  const found = candidates.find((value) => typeof value === 'string' && value.trim());
  if (found) return found;
  return 'No response text returned.';
}

function toDialog(messages: CompareMessage[]): NLTChatDialogMessage[] {
  return messages.map((msg) => ({
    type: msg.role,
    content: msg.content,
  }));
}

function selectionWithinNode(selection: Selection, node: HTMLElement | null): boolean {
  if (!node || selection.rangeCount === 0) return false;
  const range = selection.getRangeAt(0);
  const ancestor = range.commonAncestorContainer;
  return node.contains(ancestor);
}

const NLTConfigCompareChats: React.FC<NLTConfigCompareChatsProps> = ({
  mode = 'nlt',
  integrationId,
  beforeConfig,
  afterConfig,
  onQuoteToNlt,
  compareKey = 'default',
  beforeSubtitle,
  afterSubtitle,
}) => {
  const copy = MODE_COPY[mode];
  const quoteEnabled = mode === 'nlt' && Boolean(onQuoteToNlt);
  const resolvedBeforeSubtitle = beforeSubtitle ?? copy.beforeSubtitle;
  const resolvedAfterSubtitle = afterSubtitle ?? copy.afterSubtitle;
  const [messages, setMessages] = useState<Record<CompareSide, CompareMessage[]>>({
    before: [],
    after: [],
  });
  const [inputs, setInputs] = useState<Record<CompareSide, string>>({ before: '', after: '' });
  const [loading, setLoading] = useState<Record<CompareSide, boolean>>({ before: false, after: false });
  const [selectionToolbar, setSelectionToolbar] = useState<SelectionToolbarState | null>(null);
  const [nltAdminPayload, setNltAdminPayload] = useState<NltAdminConfigApiPayload | null>(null);
  const beforeRef = useRef<HTMLDivElement | null>(null);
  const afterRef = useRef<HTMLDivElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);

  const chatIds = useMemo(
    () => ({
      before: `${mode}-before-${compareKey}`,
      after: `${mode}-after-${compareKey}`,
    }),
    [mode, compareKey]
  );

  const clearSelectionToolbar = useCallback(() => setSelectionToolbar(null), []);

  useEffect(() => {
    if (mode !== 'nlt') {
      setNltAdminPayload(null);
      return;
    }
    let cancelled = false;
    void resolveNltAdminConfigForApi()
      .then((payload) => {
        if (!cancelled) setNltAdminPayload(payload);
      })
      .catch(() => {
        if (!cancelled) setNltAdminPayload(null);
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  useEffect(() => {
    setMessages({ before: [], after: [] });
    setInputs({ before: '', after: '' });
    setLoading({ before: false, after: false });
    clearSelectionToolbar();
  }, [compareKey, clearSelectionToolbar]);

  useEffect(() => {
    if (beforeRef.current) beforeRef.current.scrollTop = beforeRef.current.scrollHeight;
  }, [messages.before, loading.before]);

  useEffect(() => {
    if (afterRef.current) afterRef.current.scrollTop = afterRef.current.scrollHeight;
  }, [messages.after, loading.after]);

  const updateSelectionToolbar = useCallback(
    (side: CompareSide, container: HTMLElement | null) => {
      if (!quoteEnabled) {
        clearSelectionToolbar();
        return;
      }
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || !container) {
        clearSelectionToolbar();
        return;
      }
      if (!selectionWithinNode(selection, container)) {
        clearSelectionToolbar();
        return;
      }

      const text = selection.toString().trim();
      if (!text) {
        clearSelectionToolbar();
        return;
      }

      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      let messageId: string | undefined;
      let messageRole: NltCompareQuoteMessageRole | undefined;
      const messageEl = (range.commonAncestorContainer as Node).parentElement?.closest?.(
        '[data-message-id]'
      ) as HTMLElement | null;
      if (messageEl?.dataset.messageId) {
        messageId = messageEl.dataset.messageId;
      }
      if (messageEl?.dataset.messageRole === 'user' || messageEl?.dataset.messageRole === 'assistant') {
        messageRole = messageEl.dataset.messageRole;
      }

      setSelectionToolbar({
        text,
        side,
        messageId,
        messageRole,
        top: rect.top,
        left: rect.left + rect.width / 2,
      });
    },
    [clearSelectionToolbar, quoteEnabled]
  );

  useEffect(() => {
    const onDocMouseUp = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
        clearSelectionToolbar();
        return;
      }
      if (beforeRef.current && selectionWithinNode(selection, beforeRef.current)) {
        updateSelectionToolbar('before', beforeRef.current);
        return;
      }
      if (afterRef.current && selectionWithinNode(selection, afterRef.current)) {
        updateSelectionToolbar('after', afterRef.current);
        return;
      }
      clearSelectionToolbar();
    };

    const onDocScroll = () => clearSelectionToolbar();

    document.addEventListener('mouseup', onDocMouseUp);
    document.addEventListener('scroll', onDocScroll, true);
    return () => {
      document.removeEventListener('mouseup', onDocMouseUp);
      document.removeEventListener('scroll', onDocScroll, true);
    };
  }, [clearSelectionToolbar, updateSelectionToolbar]);

  const handleQuoteInNlt = () => {
    if (!selectionToolbar || !onQuoteToNlt) return;
    onQuoteToNlt({
      text: selectionToolbar.text,
      source: selectionToolbar.side,
      messageId: selectionToolbar.messageId,
      messageRole: selectionToolbar.messageRole,
    });
    clearSelectionToolbar();
    window.getSelection()?.removeAllRanges();
  };

  const send = async (side: CompareSide) => {
    const text = inputs[side].trim();
    if (!text || !integrationId) return;

    const config = side === 'before' ? beforeConfig : afterConfig;
    if (!config) return;
    if (mode === 'nlt' && !nltAdminPayload) return;

    const userMessage: CompareMessage = {
      id: `${side}-u-${Date.now()}`,
      role: 'user',
      content: text,
    };
    const nextMessages = [...messages[side], userMessage];
    setMessages((prev) => ({ ...prev, [side]: nextMessages }));
    setInputs((prev) => ({ ...prev, [side]: '' }));
    setLoading((prev) => ({ ...prev, [side]: true }));

    try {
      const res = await nlt.chats(
        integrationId,
        {
          config: JSON.stringify(prepareConnectorConfigForNltApi(config as Record<string, unknown>)),
          ...(mode === 'nlt' && nltAdminPayload
            ? {
                nlt_config: JSON.stringify(nltAdminPayload.nlt_config),
                ...(nltAdminPayload.nlt_config_id
                  ? { nlt_config_id: nltAdminPayload.nlt_config_id }
                  : {}),
              }
            : {}),
          dialog: toDialog(nextMessages),
          rag_enabled: true,
          ltm_enabled: true,
          diary_enabled: true,
          portrait_enabled: true,
          chat_id: chatIds[side],
        },
        { chatType: CHAT_TYPE_BY_MODE[mode] }
      );
      const reply: CompareMessage = {
        id: `${side}-a-${Date.now()}`,
        role: 'assistant',
        content: res.success ? extractReplyText(res.data) : res.error?.message || 'Failed to get reply.',
      };
      setMessages((prev) => ({ ...prev, [side]: [...prev[side], reply] }));
    } catch (e: any) {
      const reply: CompareMessage = {
        id: `${side}-err-${Date.now()}`,
        role: 'assistant',
        content: e?.response?.data?.error?.message || e?.message || 'Failed to get reply.',
      };
      setMessages((prev) => ({ ...prev, [side]: [...prev[side], reply] }));
    } finally {
      setLoading((prev) => ({ ...prev, [side]: false }));
    }
  };

  const nltAdminReady = mode !== 'nlt' || Boolean(nltAdminPayload);

  const renderChat = (
    side: CompareSide,
    title: string,
    badge: string,
    subtitle: string,
    ref: React.RefObject<HTMLDivElement>
  ) => (
    <div className={`nlt-compare-chat nlt-compare-chat--${side}`}>
      <div className="nlt-compare-chat__head">
        <div>
          <span className="nlt-compare-chat__badge">{badge}</span>
          <h4>{title}</h4>
          <p>{subtitle}</p>
        </div>
        <button
          type="button"
          className="nlt-compare-chat__clear"
          onClick={() => setMessages((prev) => ({ ...prev, [side]: [] }))}
          disabled={messages[side].length === 0 || loading[side]}
        >
          Clear
        </button>
      </div>
      <div className="nlt-compare-chat__messages" ref={ref}>
        {messages[side].length === 0 ? (
          <div className="nlt-compare-chat__empty">Send a message to test this config.</div>
        ) : (
          messages[side].map((msg) => (
            <div
              key={msg.id}
              data-message-id={msg.id}
              data-message-role={msg.role}
              className={`nlt-compare-msg nlt-compare-msg--${msg.role}`}
            >
              <div className="nlt-compare-msg__label">{chatMessageRoleLabel(msg.role)}</div>
              <div className="nlt-compare-msg__text">
                {msg.role === 'assistant' ? (
                  <ChatMarkdownBody>{msg.content}</ChatMarkdownBody>
                ) : (
                  msg.content
                )}
              </div>
            </div>
          ))
        )}
        {loading[side] && (
          <div className="nlt-compare-chat__loading">
            <span className="nlt-compare-chat__loading-dots" aria-hidden>
              <span />
              <span />
              <span />
            </span>
            Thinking…
          </div>
        )}
      </div>
      <div className="nlt-compare-chat__composer">
        <textarea
          value={inputs[side]}
          onChange={(e) => setInputs((prev) => ({ ...prev, [side]: e.target.value }))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send(side);
            }
          }}
          placeholder={`Message ${title.toLowerCase()} config…`}
          disabled={
            loading[side] ||
            !integrationId ||
            !nltAdminReady ||
            !(side === 'before' ? beforeConfig : afterConfig)
          }
          rows={2}
        />
        <button
          type="button"
          className="nlt-compare-chat__send"
          onClick={() => void send(side)}
          disabled={
            loading[side] ||
            !inputs[side].trim() ||
            !integrationId ||
            !nltAdminReady ||
            !(side === 'before' ? beforeConfig : afterConfig)
          }
          aria-label={`Send message to ${title}`}
        >
          <i className="fas fa-paper-plane" aria-hidden />
          <span className="nlt-compare-chat__send-label">Send</span>
        </button>
      </div>
    </div>
  );

  return (
    <section className="nlt-compare" ref={sectionRef}>
      <div className="nlt-compare__title">
        <h3>{copy.title}</h3>
        <p>
          {copy.description}
          {quoteEnabled ? ' Select text in a message, then quote it in NLT.' : ''}
        </p>
      </div>
      <div className="nlt-compare__grid">
        {renderChat(
          'before',
          copy.beforeTitle,
          copy.beforeBadge,
          resolvedBeforeSubtitle,
          beforeRef
        )}
        {renderChat('after', copy.afterTitle, copy.afterBadge, resolvedAfterSubtitle, afterRef)}
      </div>
      {selectionToolbar && quoteEnabled ? (
        <button
          type="button"
          className="nlt-compare-quote-toolbar"
          style={{
            top: Math.max(8, selectionToolbar.top - 8),
            left: selectionToolbar.left,
          }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleQuoteInNlt}
        >
          <i className="fas fa-quote-right" aria-hidden="true" />
          {selectionToolbar.messageRole
            ? `Quote ${chatMessageRoleLabel(selectionToolbar.messageRole)} in NLT`
            : 'Quote in NLT'}
        </button>
      ) : null}
    </section>
  );
};

export default NLTConfigCompareChats;
